// Manuale HACCP — pagina di consultazione T-060.
// VIEWER-ONLY: mostra le revisioni del Manuale HACCP del tenant e permette
// di aprire il PDF firmato. La generazione del manuale e' un comando
// INTERNO del consulente (tramite Claude/dashboard admin), NON del cliente.
//
// T-115 (2026-05-27): il gating originale T-071 era "se non sei platform owner
// → Teaser sempre", che impediva ai ristoratori di vedere il PROPRIO manuale
// già generato (caso La Terrazza di Giò: 10 revisioni nel DB, cliente non
// vedeva niente). Nuovo comportamento:
//   - membro tenant CON revisioni → ManualeViewer (lista + download PDF)
//   - membro tenant SENZA revisioni e NON owner → ManualeTeaser (CTA call)
//   - owner/consulente (sempre) → ManualeViewer + bottoni gestione/profilazione
// RLS lato DB (mr_select_own_tenant + manuals_tenant_members_read) garantisce
// che il ristoratore veda solo le revisioni del proprio tenant.

import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import {
  addOutline,
  attachOutline,
  calendarOutline,
  checkmarkCircleOutline,
  closeOutline,
  cloudUploadOutline,
  createOutline,
  documentTextOutline,
  informationCircleOutline,
  logoWhatsapp,
  mailOutline,
  openOutline,
  qrCodeOutline,
  refreshOutline,
  ribbonOutline,
  shieldCheckmarkOutline,
  sparklesOutline,
  timeOutline,
  trashOutline,
} from 'ionicons/icons';
import { useCallback, useEffect, useRef, useState } from 'react';

import { manualDocumentExtension, manualDocumentMime, validateManualDocumentFile } from './manualDocumentFile.js';

import { useAuth } from '../../lib/auth/AuthProvider.js';
import { confirmDialog, alertDialog } from '../../lib/ui/dialogs.js';
import { dbg } from '../../lib/debug/debugLog.js';
import { prepareFileUpload } from '../../lib/prepareFileUpload.js';
import { getSupabase } from '../../lib/supabase.js';
import { openBookingPage } from '../../lib/booking.js';

interface ManualRevisionRow {
  id: string;
  tenant_id: string;
  revision_number: string;
  categoria_slug: string;
  status: 'draft' | 'signed';
  pdf_storage_path: string;
  pdf_sha256: string;
  sign_ready: boolean;
  sign_check: Record<string, unknown>;
  generated_at: string;
  signed_at: string | null;
}

interface ExternalManualDocumentRow {
  id: string;
  tenant_id: string;
  description: string;
  original_filename: string;
  storage_path: string;
  pdf_sha256: string;
  file_size_bytes: number;
  uploaded_at: string;
  series_id: string;
  revision_number: number;
  supersedes_document_id: string | null;
}

function formatFileSize(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toLocaleString('it-IT', {
    maximumFractionDigits: 1,
  })} MB`;
}

function ExternalManualDocuments({
  tenantId,
  canUpload,
  canManage,
  onPresenceChange,
}: {
  tenantId: string | null;
  canUpload: boolean;
  canManage: boolean;
  onPresenceChange?: (hasDocuments: boolean) => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<ExternalManualDocumentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [revisionSource, setRevisionSource] = useState<ExternalManualDocumentRow | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    if (!tenantId) {
      setDocuments([]);
      onPresenceChange?.(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await getSupabase()
        .from('active_haccp_manual_documents')
        .select(
          'id, tenant_id, description, original_filename, storage_path, pdf_sha256, file_size_bytes, uploaded_at, series_id, revision_number, supersedes_document_id',
        )
        .eq('tenant_id', tenantId)
        .order('uploaded_at', { ascending: false });
      if (queryError) throw queryError;
      const nextDocuments = (data ?? []) as ExternalManualDocumentRow[];
      setDocuments(nextDocuments);
      onPresenceChange?.(nextDocuments.length > 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [onPresenceChange, tenantId]);

  useEffect(() => {
    void fetchDocuments();
  }, [fetchDocuments]);

  const resetUploadForm = useCallback(() => {
    setSelectedFile(null);
    setDescription('');
    setRevisionSource(null);
    setShowUploadForm(false);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const startNewDocument = useCallback(() => {
    resetUploadForm();
    setShowUploadForm(true);
  }, [resetUploadForm]);

  const startRevision = useCallback((document: ExternalManualDocumentRow) => {
    setSelectedFile(null);
    setDescription(document.description);
    setRevisionSource(document);
    setShowUploadForm(true);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const selectFile = useCallback(async (file: File | undefined) => {
    setError(null);
    if (!file) {
      setSelectedFile(null);
      return;
    }
    const validationError = await validateManualDocumentFile(file);
    if (validationError) {
      setSelectedFile(null);
      setError(validationError);
      return;
    }
    setSelectedFile(file);
  }, []);

  const uploadDocument = useCallback(async () => {
    const normalizedDescription = description.trim();
    if (!tenantId || !selectedFile) {
      setError('Seleziona il documento da caricare.');
      return;
    }
    if (normalizedDescription.length < 3) {
      setError('Inserisci una descrizione di almeno 3 caratteri.');
      return;
    }
    const firstCheck = await confirmDialog(
      revisionSource
        ? 'Verrà creata una nuova revisione. Il documento precedente resterà conservato nello storico e non sarà sovrascritto.'
        : 'Verrà aggiunto un nuovo documento separato all’archivio HACCP.',
      {
        header: revisionSource ? 'Creare la nuova revisione?' : 'Caricare il nuovo documento?',
        okText: 'Continua',
      },
    );
    if (!firstCheck) return;
    const finalCheck = await confirmDialog(
      'Hai controllato che il documento e la descrizione siano quelli corretti? Dopo il caricamento il file sarà attestato e non potrà essere sovrascritto.',
      {
        header: 'Conferma definitiva',
        okText: revisionSource ? 'Crea revisione' : 'Carica documento',
      },
    );
    if (!finalCheck) return;

    setUploading(true);
    setError(null);
    let stage = 'read';
    try {
      const supabase = getSupabase();
      const storagePath = `${tenantId}/external/${crypto.randomUUID()}.${manualDocumentExtension(selectedFile)}`;
      const { bytes, sha256: pdfSha256 } = await prepareFileUpload(selectedFile);
      stage = 'upload';
      const { error: uploadError } = await supabase.storage
        .from('manuals')
        .upload(storagePath, bytes, {
          contentType: manualDocumentMime(selectedFile)!,
          upsert: false,
          metadata: {
            source: 'external_haccp_manual',
            original_filename: selectedFile.name,
          },
        });
      if (uploadError) throw uploadError;

      stage = 'register';
      const rpcName = revisionSource
        ? 'revise_external_haccp_manual_document_v1'
        : 'register_external_haccp_manual_document';
      const { error: registerError } = await supabase.rpc(rpcName, {
        p_tenant_id: tenantId,
        ...(revisionSource ? { p_document_id: revisionSource.id } : {}),
        p_description: normalizedDescription,
        p_original_filename: selectedFile.name,
        p_storage_path: storagePath,
        p_pdf_sha256: pdfSha256,
        p_file_size_bytes: bytes.byteLength,
      });
      if (registerError) throw registerError;

      const wasRevision = revisionSource !== null;
      resetUploadForm();
      await fetchDocuments();
      await alertDialog(
        wasRevision
          ? 'La nuova revisione è ora attiva. La versione precedente resta conservata nello storico.'
          : 'Il documento è ora disponibile nella sezione documentale e ne attesta la presenza.',
        { header: wasRevision ? 'Revisione creata' : 'Documento caricato' },
      );
    } catch (e) {
      dbg.warn('manual-upload', 'upload_failed', { stage, size_bytes: selectedFile.size, error: e instanceof Error ? e.message : String(e) });
      setError(`Upload non completato: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setUploading(false);
    }
  }, [description, fetchDocuments, resetUploadForm, revisionSource, selectedFile, tenantId]);

  const openDocument = useCallback(async (document: ExternalManualDocumentRow) => {
    setOpening(document.id);
    setError(null);
    try {
      const { data, error: signedUrlError } = await getSupabase().storage
        .from('manuals')
        .createSignedUrl(document.storage_path, 60 * 60);
      if (signedUrlError || !data) {
        throw signedUrlError ?? new Error('signed_url_failed');
      }
      window.open(data.signedUrl, '_blank');
    } catch (e) {
      setError(`Impossibile aprire il documento: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setOpening(null);
    }
  }, []);

  const removeDocument = useCallback(async (document: ExternalManualDocumentRow) => {
    if (!tenantId) return;
    const firstCheck = await confirmDialog(
      'Il documento non sarà più visibile nell’archivio attivo. Il file, il suo hash e la cronologia resteranno conservati come prova di audit.',
      { header: 'Eliminare il documento?', okText: 'Continua', danger: true },
    );
    if (!firstCheck) return;
    const finalCheck = await confirmDialog(
      'Confermi definitivamente la rimozione dall’archivio attivo?',
      { header: 'Seconda conferma', okText: 'Elimina', danger: true },
    );
    if (!finalCheck) return;

    setRemoving(document.id);
    setError(null);
    try {
      const { error: removeError } = await getSupabase().rpc(
        'remove_external_haccp_manual_document_v1',
        { p_tenant_id: tenantId, p_document_id: document.id },
      );
      if (removeError) throw removeError;
      if (revisionSource?.id === document.id) resetUploadForm();
      await fetchDocuments();
      await alertDialog(
        'Il documento è stato rimosso dall’archivio attivo. Lo storico di audit resta conservato.',
        { header: 'Documento eliminato' },
      );
    } catch (e) {
      setError(`Eliminazione non completata: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRemoving(null);
    }
  }, [fetchDocuments, resetUploadForm, revisionSource?.id, tenantId]);

  const formVisible = canUpload && (showUploadForm || (!loading && documents.length === 0));
  const busy = uploading || opening !== null || removing !== null;

  return (
    <section
      data-testid="manuale-documents"
      aria-labelledby="external-manual-documents-title"
      style={{
        marginBottom: 24,
        padding: 16,
        background: '#f8fafc',
        border: '1px solid #cbd5e1',
        borderRadius: 14,
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div
          aria-hidden="true"
          style={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            borderRadius: 10,
            color: '#1d4ed8',
            background: '#dbeafe',
          }}
        >
          <IonIcon icon={attachOutline} style={{ fontSize: 22 }} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2
            id="external-manual-documents-title"
            style={{ fontSize: 17, color: '#0f172a', margin: '0 0 4px' }}
          >
            Manuali e documenti caricati
          </h2>
          <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.5, margin: 0 }}>
            Carica PDF, JPEG/JPG o PNG oppure aggiorna un documento creando una nuova
            revisione. Le versioni già attestate non vengono sovrascritte.
          </p>
        </div>
      </div>

      {canUpload && !formVisible && (
        <IonButton
          expand="block"
          fill="outline"
          style={{ minHeight: 44, marginTop: 16 }}
          disabled={busy}
          onClick={startNewDocument}
        >
          <IonIcon icon={addOutline} slot="start" />
          Carica nuovo documento
        </IonButton>
      )}

      {formVisible && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void uploadDocument();
          }}
          style={{
            marginTop: 16,
            padding: 14,
            display: 'grid',
            gap: 12,
            background: '#fff',
            border: '1px solid #dbeafe',
            borderRadius: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
              {revisionSource ? `Nuova revisione · Rev. ${revisionSource.revision_number + 1}` : 'Nuovo documento'}
            </div>
            <div style={{ marginTop: 4, fontSize: 12, lineHeight: 1.45, color: '#475569' }}>
              {revisionSource
                ? 'Sostituisce la versione visibile; il documento precedente resta nello storico.'
                : 'Crea una voce separata nell’archivio.'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
              1. Seleziona il documento (PDF, JPG o PNG)
            </div>
            <label
              htmlFor="external-manual-file"
              style={{
                minHeight: 44,
                padding: '10px 14px',
                border: '1.5px solid #2563eb',
                borderRadius: 10,
                color: '#1d4ed8',
                background: '#fff',
                fontSize: 14,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                cursor: uploading ? 'not-allowed' : 'pointer',
                opacity: uploading ? 0.6 : 1,
              }}
            >
              <IonIcon icon={documentTextOutline} style={{ fontSize: 20 }} />
              {selectedFile ? 'Cambia file' : revisionSource ? 'Scegli il file revisionato' : 'Scegli file'}
            </label>
            <input
              ref={inputRef}
              id="external-manual-file"
              type="file"
              accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
              disabled={uploading}
              style={{ position: 'absolute', width: 1, height: 1, opacity: 0 }}
              onChange={(event) => void selectFile(event.target.files?.[0])}
            />
            {selectedFile && (
              <div style={{ fontSize: 12, color: '#334155', marginTop: 6, overflowWrap: 'anywhere' }}>
                {selectedFile.name} · {formatFileSize(selectedFile.size)}
              </div>
            )}
          </div>

          <IonTextarea
            label="2. Descrizione del documento"
            labelPlacement="stacked"
            fill="outline"
            value={description}
            maxlength={240}
            counter
            autoGrow
            disabled={uploading}
            helperText="Esempio: Manuale HACCP 2026, Procedure di sanificazione, Allegato allergeni."
            onIonInput={(event) => setDescription(event.detail.value ?? '')}
          />

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: documents.length > 0 ? 'repeat(auto-fit, minmax(150px, 1fr))' : '1fr',
              gap: 8,
            }}
          >
            {documents.length > 0 && (
              <IonButton
                type="button"
                expand="block"
                fill="clear"
                style={{ minHeight: 44, margin: 0 }}
                disabled={uploading}
                onClick={resetUploadForm}
              >
                <IonIcon icon={closeOutline} slot="start" />
                Annulla
              </IonButton>
            )}
            <IonButton
              type="submit"
              expand="block"
              style={{ minHeight: 44, margin: 0 }}
              disabled={uploading || !selectedFile || description.trim().length < 3}
            >
              {uploading ? (
                <>
                  <IonSpinner name="dots" slot="start" />
                  Verifica e caricamento…
                </>
              ) : (
                <>
                  <IonIcon icon={cloudUploadOutline} slot="start" />
                  {revisionSource ? 'Crea revisione' : 'Carica documento'}
                </>
              )}
            </IonButton>
          </div>
        </form>
      )}

      {error && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            marginTop: 12,
            padding: 10,
            border: '1px solid #fecaca',
            borderRadius: 8,
            color: '#991b1b',
            background: '#fef2f2',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
          Archivio attivo ({documents.length})
        </div>
        {loading && <IonSpinner name="dots" />}
        {!loading && documents.length === 0 && (
          <div style={{ fontSize: 13, color: '#64748b' }}>
            Nessun documento esterno ancora archiviato. Seleziona il primo documento da caricare.
          </div>
        )}
        {!loading && documents.length > 0 && (
          <div style={{ display: 'grid', gap: 10 }}>
            {documents.map((document) => (
              <article
                key={document.id}
                style={{
                  padding: 14,
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                }}
              >
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <IonIcon
                    icon={documentTextOutline}
                    color="primary"
                    aria-hidden="true"
                    style={{ flexShrink: 0, fontSize: 24, marginTop: 2 }}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 750, color: '#0f172a' }}>
                      {document.description}
                    </div>
                    <div style={{ marginTop: 3, fontSize: 13, color: '#475569', overflowWrap: 'anywhere' }}>
                      {document.original_filename} · {formatFileSize(document.file_size_bytes)}
                    </div>
                    <div style={{ marginTop: 3, fontSize: 12, color: '#64748b' }}>
                      Revisione {document.revision_number} · {new Date(document.uploaded_at).toLocaleString('it-IT')}
                      {' · '}SHA-256 {document.pdf_sha256.slice(0, 12)}…
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    marginTop: 12,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: 8,
                  }}
                >
                  <IonButton
                    expand="block"
                    fill="outline"
                    style={{ minHeight: 44, margin: 0 }}
                    disabled={busy}
                    onClick={() => void openDocument(document)}
                  >
                    {opening === document.id
                      ? <IonSpinner name="dots" slot="start" />
                      : <IonIcon icon={openOutline} slot="start" />}
                    Apri
                  </IonButton>
                  {canManage && (
                    <IonButton
                      expand="block"
                      fill="outline"
                      style={{ minHeight: 44, margin: 0 }}
                      disabled={busy}
                      onClick={() => startRevision(document)}
                    >
                      <IonIcon icon={createOutline} slot="start" />
                      Modifica
                    </IonButton>
                  )}
                  {canManage && (
                    <IonButton
                      expand="block"
                      fill="outline"
                      color="danger"
                      style={{ minHeight: 44, margin: 0 }}
                      disabled={busy}
                      onClick={() => void removeDocument(document)}
                    >
                      {removing === document.id
                        ? <IonSpinner name="dots" slot="start" />
                        : <IonIcon icon={trashOutline} slot="start" />}
                      Elimina
                    </IonButton>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// T-071: pagina mostrata ai tenant che NON hanno (ancora) il Manuale HACCP
// gestito su TrackEAT. Spiega cos'è il servizio, perché è meglio averlo in
// app, e li invita a richiedere una consulenza. Sostituisce la card di
// gestione interna che resta visibile solo al platform owner.
function ManualeTeaser({
  tenantId,
  canUploadExternal,
}: {
  tenantId: string | null;
  canUploadExternal: boolean;
}): JSX.Element {
  const subject = encodeURIComponent('Richiesta Manuale HACCP per il mio locale');
  const body = encodeURIComponent(
    [
      "Ciao Pierpaolo,",
      '',
      'Sono interessato/a a far redigere il Manuale di Autocontrollo HACCP del mio locale tramite TrackEAT.',
      '',
      'Puoi richiamarmi per spiegarmi il servizio e i tempi?',
      '',
      'Grazie!',
    ].join('\n'),
  );
  const mailto = `mailto:criscenzo.pierpaolo@gmail.com?subject=${subject}&body=${body}`;
  // Numero WhatsApp dedicato TrackEAT (vedi memoria "WhatsApp v2 / hosting bot").
  // Se in futuro cambia, basta aggiornare qui.
  const whatsappUrl = 'https://wa.me/393486382343?text=' + encodeURIComponent(
    'Ciao Pierpaolo, sono interessato/a al Manuale HACCP tramite TrackEAT. Mi puoi spiegare come funziona?',
  );
  const [hasExternalDocuments, setHasExternalDocuments] = useState<boolean | null>(null);
  const showDescription = hasExternalDocuments === false;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/today" />
          </IonButtons>
          <IonTitle>Manuale HACCP</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: 16 }}>
          {/* Hero */}
          {showDescription && (
            <div
              data-testid="manuale-hero"
              style={{
                background: 'linear-gradient(135deg, #0066cc 0%, #1e40af 100%)',
                color: '#fff',
                borderRadius: 16,
                padding: '22px 20px',
                marginBottom: 20,
                boxShadow: '0 4px 16px rgba(0, 102, 204, 0.25)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <IonIcon icon={ribbonOutline} style={{ fontSize: 20 }} />
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.92 }}>
                  Servizio TrackEAT Premium
                </span>
              </div>
              <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 8px', lineHeight: 1.2 }}>
                Il tuo Manuale HACCP, dentro l'app
              </h1>
              <p style={{ fontSize: 14, margin: 0, opacity: 0.95, lineHeight: 1.5 }}>
                Niente più raccoglitori in cucina o cartelline perse. Il consulente
                HACCP redige il manuale del tuo locale e lo firma digitalmente:
                tu lo hai sempre con te, e l'ASL lo trova in 2 secondi via QR.
              </p>
            </div>
          )}

          <ExternalManualDocuments
            tenantId={tenantId}
            canUpload={canUploadExternal}
            canManage={false}
            onPresenceChange={setHasExternalDocuments}
          />

          {showDescription && (
            <div data-testid="manuale-description">
          {/* Benefit list */}
          <h2 style={{ fontSize: 13, fontWeight: 700, color: '#475569', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 12px' }}>
            Perché averlo qui
          </h2>
          <div style={{ display: 'grid', gap: 10, marginBottom: 24 }}>
            {[
              {
                icon: shieldCheckmarkOutline,
                title: 'Conforme per legge',
                body: 'Reg. CE 852/2004 Art. 5 + Allegato II. Redatto e firmato dal consulente, riconosciuto dall\'ASL.',
              },
              {
                icon: qrCodeOutline,
                title: 'Pronto in 2 secondi all\'ASL',
                body: 'Un QR sulla porta della cucina, il funzionario scansiona e vede l\'ultima revisione firmata.',
              },
              {
                icon: timeOutline,
                title: 'Sempre aggiornato',
                body: 'Cambia il responsabile, un fornitore, il menu: il consulente aggiorna la revisione, ti arriva subito in app.',
              },
              {
                icon: sparklesOutline,
                title: 'Tutto integrato',
                body: 'Lavora insieme alle scadenze, alle temperature, alle etichette: un solo posto, zero scartoffie.',
              },
              {
                icon: documentTextOutline,
                title: 'Storico immutabile',
                body: 'Ogni revisione è marcata temporalmente (RFC 3161) e ancorata nella catena eventi HACCP: prova in caso di controllo.',
              },
            ].map((b) => (
              <div
                key={b.title}
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  padding: 14,
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: '#dbeafe',
                    color: '#0066cc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IonIcon icon={b.icon} style={{ fontSize: 20 }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 2 }}>{b.title}</div>
                  <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.4 }}>{b.body}</div>
                </div>
              </div>
            ))}
          </div>

          {/* How it works */}
          <h2 style={{ fontSize: 13, fontWeight: 700, color: '#475569', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 12px' }}>
            Come funziona
          </h2>
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: 16,
              marginBottom: 24,
            }}
          >
            {[
              { n: 1, title: 'Richiedi una consulenza', body: 'Scrivimi via email o WhatsApp: ti faccio un sopralluogo (anche da remoto) e raccolgo i dati del tuo locale.' },
              { n: 2, title: 'Profilo del tuo locale', body: 'Compiliamo organigramma, planimetria, fornitori, attrezzature e flussi di lavoro. Una volta sola, il resto si aggiorna in app.' },
              { n: 3, title: 'Manuale firmato e caricato', body: 'Genero il PDF conforme, lo firmo digitalmente come consulente, lo carico qui. Ti arriva una notifica e il QR è pronto.' },
              { n: 4, title: 'Tienilo aggiornato', body: 'Cambi un fornitore o il responsabile? Mi avvisi (anche dall\'app), genero una nuova revisione: la vecchia resta come storico.' },
            ].map((s, i, arr) => (
              <div
                key={s.n}
                style={{
                  display: 'flex',
                  gap: 14,
                  paddingBottom: i === arr.length - 1 ? 0 : 14,
                  borderBottom: i === arr.length - 1 ? 'none' : '1px solid #f1f5f9',
                  marginBottom: i === arr.length - 1 ? 0 : 14,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    background: '#0066cc',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {s.n}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 2 }}>{s.title}</div>
                  <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.4 }}>{s.body}</div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <h2 style={{ fontSize: 13, fontWeight: 700, color: '#475569', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 12px' }}>
            Pronto a metterti in regola?
          </h2>
          <div style={{ display: 'grid', gap: 10, marginBottom: 24 }}>
            {/* T-076: Prenota call — riusa il booking widget del sito web
                (trackeat.eu/#prenota → modal cal.com-style su edge functions
                booking-slots/booking-create già live). CTA primario. */}
            <button
              type="button"
              onClick={() => { void openBookingPage(); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                background: '#0066cc',
                color: '#fff',
                padding: '14px 16px',
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 15,
                border: 0,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0, 102, 204, 0.3)',
              }}
            >
              <IonIcon icon={calendarOutline} style={{ fontSize: 22 }} />
              Prenota una call
            </button>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                background: '#25d366',
                color: '#fff',
                padding: '14px 16px',
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 15,
                textDecoration: 'none',
                boxShadow: '0 2px 8px rgba(37, 211, 102, 0.3)',
              }}
            >
              <IonIcon icon={logoWhatsapp} style={{ fontSize: 22 }} />
              Scrivimi su WhatsApp
            </a>
            <a
              href={mailto}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                background: '#fff',
                color: '#0066cc',
                border: '1.5px solid #0066cc',
                padding: '14px 16px',
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 15,
                textDecoration: 'none',
              }}
            >
              <IonIcon icon={mailOutline} style={{ fontSize: 20 }} />
              Manda una email
            </a>
          </div>

          {/* Reassurance footer */}
          <div
            style={{
              padding: '12px 14px',
              background: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: 10,
              fontSize: 12,
              color: '#075985',
              lineHeight: 1.5,
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <IonIcon icon={checkmarkCircleOutline} style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }} />
            <div>
              <strong>Nessun impegno.</strong> Ti spiego il servizio, ti faccio
              un preventivo personalizzato in base al tuo locale (cucina,
              bar, pasticceria, gelateria, …) e decidi tu. Se hai già un
              manuale cartaceo, lo digitalizziamo.
            </div>
          </div>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}

// T-071: dispatcher di alto livello. Decide quale vista mostrare in base
// all'identità (platform owner vs altri tenant). Separato dal componente
// Wrapper: carica le revisioni del tenant attivo, poi decide se mostrare
// il Teaser (nessuna revisione + non sei owner) o il Viewer (revisioni
// esistenti, oppure sei owner anche con DB vuoto). Hook unconditionally
// chiamati nel componente Viewer.
export function ManualePage(): JSX.Element {
  return <ManualeViewer />;
}

// Vista del manuale HACCP — accessibile a TUTTI i membri del tenant via RLS.
// Mostra le revisioni del tenant attivo e il PDF apribile.
// I bottoni di gestione (profilazione attività) sono visibili solo
// all'owner/consulente.
function ManualeViewer(): JSX.Element {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const isOwnerOrConsulente =
    profile?.is_platform_owner === true || profile?.role === 'consulente';
  const canUploadExternal = isOwnerOrConsulente || profile?.role === 'ristoratore';
  const [rows, setRows] = useState<ManualRevisionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  // T-120: stato del flusso firma. `signing` = revisione in corso di firma;
  // `signError` = lista [{field,reason}] in caso di validation_failed (422).
  const [signing, setSigning] = useState<string | null>(null);
  const [signError, setSignError] = useState<
    { revisionNumber: string; missing: Array<{ field: string; reason: string }> }
    | null
  >(null);

  const fetchRevisions = useCallback(async () => {
    if (!tenantId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase
        .from('manual_revisions')
        .select(
          'id, tenant_id, revision_number, categoria_slug, status, pdf_storage_path, pdf_sha256, sign_ready, sign_check, generated_at, signed_at',
        )
        .eq('tenant_id', tenantId)
        .order('generated_at', { ascending: false });
      if (err) throw err;
      setRows((data ?? []) as ManualRevisionRow[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void fetchRevisions();
  }, [fetchRevisions]);

  const openPdf = useCallback(async (row: ManualRevisionRow) => {
    setOpening(row.id);
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase.storage
        .from('manuals')
        .createSignedUrl(row.pdf_storage_path, 60 * 60);
      if (err || !data) throw err ?? new Error('signed_url_failed');
      window.open(data.signedUrl, '_blank');
    } catch (e) {
      await alertDialog(`Errore apertura PDF: ${e instanceof Error ? e.message : e}`, { header: 'Errore' });
    } finally {
      setOpening(null);
    }
  }, []);

  // T-120: firma di una revisione draft (consulente/owner). La RPC
  // sign_manual_revision verifica caller, rivalida l'anagrafica DB, emette
  // l'evento manual_signed nella hash chain e UPDATE manual_revisions →
  // signed. Su validation_failed (422) mostriamo un modal con la lista dei
  // campi mancanti.
  const signRevision = useCallback(
    async (row: ManualRevisionRow) => {
      if (row.status !== 'draft') return;
      if (!row.sign_ready) {
        await alertDialog(
          'Questa bozza non contiene una verifica completa dei requisiti di firma. Rigenera il PDF dopo aver completato i dati richiesti.',
          { header: 'Bozza non firmabile' },
        );
        return;
      }
      const ok = await confirmDialog(
        [
          "L'operazione:",
          '• emette un evento manual_signed nella hash chain',
          '• rende la revisione immutabile (status=signed)',
          '• sarà ancorata via RFC 3161 al prossimo cron settimanale',
          '',
          'Conferma solo se il PDF è stato già revisionato e approvato.',
        ].join('<br>'),
        { header: `Firmare la revisione ${row.revision_number}?`, okText: 'Firma' },
      );
      if (!ok) return;
      setSigning(row.id);
      setSignError(null);
      try {
        const supabase = getSupabase();
        const { data, error: rpcErr } = await supabase.rpc(
          'sign_manual_revision',
          { p_revision_id: row.id },
        );
        if (rpcErr) {
          // PostgreSQL error code/text decoder: validation_failed arriva come
          // SQLSTATE 23514 (check_violation) con detail = il jsonb del missing[].
          const msg = rpcErr.message || '';
          const detail = (rpcErr as { details?: string }).details ?? '';
          if (msg.includes('validation_failed') || detail.includes('"missing"')) {
            let missing: Array<{ field: string; reason: string }> = [];
            try {
              const parsed = JSON.parse(detail) as {
                missing?: Array<{ field: string; reason: string }>;
              };
              missing = parsed.missing ?? [];
            } catch {
              /* fallback: messaggio generico */
            }
            setSignError({
              revisionNumber: row.revision_number,
              missing: missing.length > 0
                ? missing
                : [{ field: 'unknown', reason: msg }],
            });
            return;
          }
          if (msg.includes('unauthorized')) {
            await alertDialog(
              'Solo il consulente HACCP o il platform owner può firmare le revisioni.',
              { header: 'Non autorizzato' },
            );
            return;
          }
          if (msg.includes('already_signed')) {
            await alertDialog('Questa revisione risulta già firmata. Ricarico la lista.');
            await fetchRevisions();
            return;
          }
          if (msg.includes('revision_not_ready')) {
            await alertDialog(
              'Questa bozza deve essere rigenerata prima della firma.',
              { header: 'Bozza non firmabile' },
            );
            await fetchRevisions();
            return;
          }
          throw rpcErr;
        }
        // Success: refetch + toast
        await fetchRevisions();
        const result = data as { revision_number?: string } | null;
        await alertDialog(
          `Revisione ${result?.revision_number ?? row.revision_number} firmata.`,
          { header: 'Revisione firmata' },
        );
      } catch (e) {
        await alertDialog(
          `Errore firma: ${e instanceof Error ? e.message : String(e)}`,
          { header: 'Errore firma' },
        );
      } finally {
        setSigning(null);
      }
    },
    [fetchRevisions],
  );

  // T-115: se il ristoratore non ha ancora un manuale generato per il proprio
  // tenant → mostra ManualeTeaser (CTA "richiedi consulenza"). Se ha almeno
  // una revisione → viewer standard. Owner/consulente vede SEMPRE il viewer
  // anche con DB vuoto (deve poter accedere alla profilazione attività per
  // generare la prima revisione del cliente).
  if (!loading && !error && rows.length === 0 && !isOwnerOrConsulente) {
    return (
      <ManualeTeaser
        tenantId={tenantId}
        canUploadExternal={canUploadExternal}
      />
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/today" />
          </IonButtons>
          <IonTitle>Manuale HACCP</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => void fetchRevisions()}>
              <IonIcon slot="icon-only" icon={refreshOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher
          slot="fixed"
          onIonRefresh={(e) => {
            void fetchRevisions().finally(() => e.detail.complete());
          }}
        >
          <IonRefresherContent />
        </IonRefresher>

        <div style={{ padding: 16 }}>
          <h2 style={{ fontSize: 20, margin: '0 0 8px' }}>
            Manuale di Autocontrollo HACCP
          </h2>
          <p style={{ fontSize: 14, color: '#666', margin: '0 0 16px' }}>
            Qui trovi sia le revisioni prodotte in TrackEAT sia i documenti caricati
            in PDF, JPEG/JPG o PNG. Tieni la documentazione a disposizione
            dell'Autorità sanitaria.
          </p>

          <ExternalManualDocuments
            tenantId={tenantId}
            canUpload={canUploadExternal}
            canManage={isOwnerOrConsulente}
          />

          <div
            style={{
              padding: 12,
              background: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: 8,
              fontSize: 13,
              color: '#075985',
              marginBottom: 16,
            }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <IonIcon
                icon={informationCircleOutline}
                aria-hidden="true"
                style={{ flexShrink: 0, fontSize: 18, marginTop: 1 }}
              />
              <div>
                <strong>Come funzionano le modifiche</strong>
                <br />
                Un documento caricato non viene sovrascritto: il pulsante
                {' '}<strong>Modifica</strong> crea una nuova revisione e conserva
                la precedente nello storico. La generazione e la firma delle
                revisioni TrackEAT restano riservate al consulente.
              </div>
            </div>
          </div>

          {/* T-115: la profilazione è il flusso interno consulente per
              raccogliere i dati strutturati (organigramma, planimetria,
              fornitori, attrezzature) prima di generare una nuova revisione.
              Visibile solo a owner/consulente. Per il ristoratore, il
              riquadro "ℹ️ Informazione" sopra è sufficiente. */}
          {isOwnerOrConsulente && (
            <IonButton
              expand="block"
              fill="outline"
              routerLink="/haccp/manuale/profilo"
            >
              Profilazione attività (riservata al consulente)
            </IonButton>
          )}

          <h3 style={{ fontSize: 16, margin: '24px 0 8px' }}>
            Revisioni generate da TrackEAT
          </h3>
          {loading && <IonSpinner />}
          {error && (
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                padding: 12,
                color: '#b91c1c',
              }}
            >
              Errore: {error}
            </div>
          )}
          {!loading && !error && rows.length === 0 && (
            <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
              Nessuna revisione del Manuale è ancora stata generata per
              questa attività.
              <br />
              <br />
              Il consulente HACCP genererà la prima revisione una volta
              completata la <strong>profilazione attività</strong>.
            </div>
          )}
          {!loading && rows.length > 0 && (
            <IonList lines="full">
              {rows.map((r) => (
                <IonItem
                  key={r.id}
                  disabled={opening === r.id || signing === r.id}
                >
                  <IonIcon icon={documentTextOutline} slot="start" />
                  <IonLabel onClick={() => void openPdf(r)} style={{ cursor: 'pointer' }}>
                    <h2>
                      Revisione {r.revision_number}{' '}
                      <IonNote
                        style={{
                          marginLeft: 8,
                          padding: '2px 8px',
                          borderRadius: 12,
                          fontSize: 11,
                          background:
                            r.status === 'signed' ? '#dcfce7' : '#fef3c7',
                          color:
                            r.status === 'signed' ? '#166534' : '#92400e',
                        }}
                      >
                        {r.status === 'signed' ? 'Firmata' : 'Bozza'}
                      </IonNote>
                    </h2>
                    <p style={{ fontSize: 12 }}>
                      {r.status === 'signed' && r.signed_at
                        ? `Firmata il ${new Date(r.signed_at).toLocaleString(
                            'it-IT',
                          )}`
                        : `Generata il ${new Date(
                            r.generated_at,
                          ).toLocaleString('it-IT')}`}
                    </p>
                    <p
                      style={{
                        fontSize: 11,
                        color: '#999',
                        fontFamily: 'monospace',
                      }}
                    >
                      SHA-256: {r.pdf_sha256.slice(0, 16)}…
                    </p>
                  </IonLabel>
                  {/* T-120: bottone "Firma" visibile solo a owner/consulente
                      su revisioni in stato draft. Su validation_failed
                      mostriamo il modal con i campi mancanti. */}
                  {isOwnerOrConsulente && r.status === 'draft' && (
                    <IonButton
                      slot="end"
                      size="small"
                      color="success"
                      disabled={signing === r.id || !r.sign_ready}
                      onClick={() => void signRevision(r)}
                    >
                      {signing === r.id
                        ? <IonSpinner name="dots" />
                        : r.sign_ready ? 'Firma' : 'Da rigenerare'}
                    </IonButton>
                  )}
                  {opening === r.id ? (
                    <IonSpinner slot="end" />
                  ) : (
                    <IonIcon
                      icon={openOutline}
                      slot="end"
                      onClick={() => void openPdf(r)}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                </IonItem>
              ))}
            </IonList>
          )}

          {/* T-120: modal su validation_failed — mostra i campi mancanti
              dall'anagrafica DB upstream (validate_revision_for_build). */}
          {signError && (
            <div
              role="dialog"
              aria-modal="true"
              onClick={() => setSignError(null)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: 16,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: 20,
                  maxWidth: 480,
                  width: '100%',
                  maxHeight: '80vh',
                  overflow: 'auto',
                  boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
                }}
              >
                <h2 style={{ margin: 0, fontSize: 18, color: '#b91c1c' }}>
                  Firma bloccata — profilazione incompleta
                </h2>
                <p style={{ fontSize: 13, color: '#475569', margin: '8px 0 16px' }}>
                  La revisione <strong>{signError.revisionNumber}</strong> non
                  può essere firmata perché alcuni dati anagrafici del locale
                  sono incompleti o ancora placeholder. Completa i campi sotto
                  in <em>Profilazione attività</em> o in <em>Dati cliente</em>{' '}
                  (admin), poi riprova.
                </p>
                <ul style={{ paddingLeft: 18, margin: '0 0 16px' }}>
                  {signError.missing.map((m, i) => (
                    <li key={i} style={{ marginBottom: 8 }}>
                      <strong style={{ color: '#dc2626' }}>{m.field}</strong>
                      <div style={{ fontSize: 13, color: '#475569' }}>
                        {m.reason}
                      </div>
                    </li>
                  ))}
                </ul>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <IonButton fill="outline" onClick={() => setSignError(null)}>
                    Chiudi
                  </IonButton>
                  <IonButton routerLink="/haccp/manuale/profilo">
                    Apri profilazione
                  </IonButton>
                </div>
              </div>
            </div>
          )}

          <div
            style={{
              marginTop: 32,
              padding: 12,
              background: '#f9fafb',
              borderRadius: 8,
              fontSize: 12,
              color: '#666',
            }}
          >
            <strong>Nota tecnica</strong>: ogni PDF firmato è immutabile, il
            suo hash SHA-256 è ancorato nella catena di eventi HACCP
            append-only e marcato temporalmente settimanalmente via FreeTSA
            (standard RFC 3161). Vedi CAP 5 §5.2 del manuale per i dettagli.
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
